import { Link } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import {
  inputClassName,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

function SignupView({
  backendUrl,
  signupStatus,
  isFirebaseConfigured,
  isSubmitting,
  form,
  onChange,
  onSubmit,
}) {
  const statusTone =
    signupStatus.type === 'loading'
      ? 'loading'
      : signupStatus.type === 'error'
        ? 'error'
        : signupStatus.type === 'success'
          ? 'success'
          : 'neutral';

  return (
    <section className="w-full max-w-[900px] text-app-ink">
      <PageHero
        eyebrow="Family Copilot"
        title="Create account"
        lede="Register with Firebase email and password, then create the matching Family Copilot backend profile."
        bearyMessage={signupStatus.type === 'loading' ? 'Beary is creating your family workspace.' : undefined}
        bearyPreset="loading"
        action={<StatusPill tone={statusTone}>{signupStatus.message}</StatusPill>}
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <SectionCard className="bg-app-shell">
          <SectionTitle
            title="Account details"
            copy="Profile fields are optional except for email and password."
          />

          {!isFirebaseConfigured ? (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              Firebase setup is required before signup will work.
            </div>
          ) : null}

          <form className="mt-5 space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="signup-email">
                Email
              </label>
              <input
                id="signup-email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => onChange('email', event.target.value)}
                placeholder="parent@example.com"
                className={inputClassName}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="signup-first-name">
                  First name
                </label>
                <input
                  id="signup-first-name"
                  value={form.firstName}
                  onChange={(event) => onChange('firstName', event.target.value)}
                  placeholder="Parent"
                  className={inputClassName}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="signup-last-name">
                  Last name
                </label>
                <input
                  id="signup-last-name"
                  value={form.lastName}
                  onChange={(event) => onChange('lastName', event.target.value)}
                  placeholder="Name"
                  className={inputClassName}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="signup-phone">
                Phone
              </label>
              <input
                id="signup-phone"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={(event) => onChange('phone', event.target.value)}
                placeholder="+15551234567"
                className={inputClassName}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="signup-kids-names">
                Kids' names
              </label>
              <input
                id="signup-kids-names"
                value={form.kidsNames}
                onChange={(event) => onChange('kidsNames', event.target.value)}
                placeholder="Ava, Noah"
                className={inputClassName}
                disabled={isSubmitting}
              />
              <p className="m-0 text-xs leading-5 text-app-muted">
                Separate multiple kids with commas. This helps personalize reminders and family context.
              </p>
            </div>

            <label
              className="flex items-start gap-3 rounded-2xl border border-app-border bg-app-surface px-4 py-3"
              htmlFor="signup-location-tracking"
            >
              <input
                id="signup-location-tracking"
                type="checkbox"
                checked={Boolean(form.locationTrackingEnabled)}
                onChange={(event) => onChange('locationTrackingEnabled', event.target.checked)}
                className="mt-1 h-4 w-4 accent-app-accent"
                disabled={isSubmitting}
              />
              <span>
                <span className="block text-sm font-semibold text-app-ink">Allow location tracking</span>
                <span className="mt-1 block text-xs leading-5 text-app-muted">
                  Store consent and, when available, your current location for location-aware reminders.
                </span>
              </span>
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="signup-password">
                  Password
                </label>
                <input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(event) => onChange('password', event.target.value)}
                  placeholder="Create a password"
                  className={inputClassName}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="signup-confirm-password">
                  Confirm password
                </label>
                <input
                  id="signup-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={(event) => onChange('confirmPassword', event.target.value)}
                  placeholder="Repeat password"
                  className={inputClassName}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
                type="submit"
                disabled={isSubmitting}
              >
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                <span>{isSubmitting ? 'Creating account...' : 'Sign up'}</span>
              </button>

              <Link
                className="inline-flex items-center rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                to="/login"
              >
                Log in
              </Link>
            </div>
          </form>
        </SectionCard>

        <SectionCard>
          <SectionTitle title="Backend" copy="Signup will create the Firebase user first, then the backend record." />
          <p className="mt-5 break-all text-sm font-medium text-app-ink">{backendUrl}</p>
        </SectionCard>
      </div>
    </section>
  );
}

export default SignupView;
